import { useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/use-toast';

// Same pause as the agent configuration page's auto-save.
const SAVE_DELAY_MS = 700;

/**
 * Runs `save` once edits pause and shows a toast if it fails.
 * It always calls the latest `save`, so a pending save never sends stale agent data,
 * and a pending save still runs on unmount (e.g. when the settings drawer closes).
 */
export function useDebouncedAgentSave<Args extends unknown[]>(
  save: (...args: Args) => Promise<void>
) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const saveRef = useRef(save);
  saveRef.current = save;

  const saveLater = useMemo(
    () =>
      debounce(async (...args: Args) => {
        try {
          await saveRef.current(...args);
        } catch {
          toast({
            variant: 'destructive',
            title: t('SAVE_FAILED', { defaultValue: 'Save failed' }),
            description: t('AGENT_CONFIGURATION_COULD_NOT_BE_SAVED', {
              defaultValue: 'Your changes could not be saved. Please try again.',
            }),
          });
        }
      }, SAVE_DELAY_MS),
    [t, toast]
  );

  useEffect(
    () => () => {
      saveLater.flush();
    },
    [saveLater]
  );

  return saveLater;
}
